import React, {useCallback, useEffect, useMemo, useState} from 'react';
import axios from 'axios';
import {useTranslation} from 'react-i18next';
import {Pagination} from '@mui/material';
import {DateTimePicker} from '@mui/x-date-pickers/DateTimePicker';

import {AuditLogEntry, LogQuery, PagedResult} from '../../../models/models';
import {useApi} from '../../../hooks/UseApi';
import {PaginationWrapper, StyledTableContainer,} from '../../../components/common/TableContainer.styled';
import {ActionButton} from '../../../components/common/ActionButtons';
import PageSizeSelector from '../../../components/common/PageSizeSelector';
import {
    ActionField,
    FieldLabel,
    FiltersBar,
    Label,
    PageSizeField,
    TabButton,
    Table,
    TabsBar,
    Td,
    Th,
} from './Logs.styled';
import {Header} from '../AdminDataSourceList/AdminDataSourceList.styled';

type LogTypeLocal = 'AUDIT' | 'TECHNICAL';

const DEFAULT_PAGE_SIZE = 50;
const TECHNICAL_LOGS_SEARCH_TAGS = 'technicalLogsSearchTags';

const loadInitialTechnicalTags = (): string[] => {
    try {
        const saved = sessionStorage.getItem(TECHNICAL_LOGS_SEARCH_TAGS);
        return saved ? JSON.parse(saved) : [];
    } catch {
        return [];
    }
};

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

const formatDateTimeLocal = (d: Date) => {
    const yyyy = d.getFullYear();
    const MM = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
};

const formatDateOnly = (d: Date) => {
    const yyyy = d.getFullYear();
    const MM = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    return `${yyyy}-${MM}-${dd}`;
};

const getLevel = (line: string): 'ERROR' | 'WARN' | 'INFO' | 'DEBUG' | undefined => {
    if (/\bERROR\b/i.test(line)) return 'ERROR';
    if (/\bWARN(ING)?\b/i.test(line)) return 'WARN';
    if (/\bINFO\b/i.test(line)) return 'INFO';
    if (/\bDEBUG\b/i.test(line)) return 'DEBUG';
    return undefined;
};

{/* [AI-769] [To be possibly uncommented in the future] */}

const Logs = () => {
    const {t, i18n} = useTranslation();
    const {loading} = useApi();

    const [type, setType] = useState<LogTypeLocal>('AUDIT');

    const [fromInput, setFromInput] = useState<string>('');
    const [toInput, setToInput] = useState<string>('');
    const [filters, setFilters] = useState<{ from?: string; to?: string }>({});

    const [page, setPage] = useState<number>(0);
    const [size, setSize] = useState<number>(DEFAULT_PAGE_SIZE);
    const [auditData, setAuditData] = useState<PagedResult<AuditLogEntry> | null>(null);

    const [technicalPreview, setTechnicalPreview] = useState<string>('');
    const [technicalDate, setTechnicalDate] = useState<string>('');

    const [technicalSearchTags, setTechnicalSearchTags] = useState<string[]>(loadInitialTechnicalTags);

    const lang = useMemo(
        () => (i18n.language || 'en').split('-')[0].toLowerCase(),
        [i18n.language],
    );
    const is12h = useMemo(() => lang === 'en', [lang]);
    const dateTimeFormat = useMemo(
        () => (lang === 'pl' ? 'dd.MM.yyyy HH:mm' : 'MM/dd/yyyy hh:mm a'),
        [lang],
    );
    const dateFormat = useMemo(
        () => (lang === 'pl' ? 'dd.MM.yyyy' : 'MM/dd/yyyy'),
        [lang],
    );

    const query: LogQuery = useMemo(() => {
        const q: LogQuery = {page, size};

        if (filters.from) {
            q.from = new Date(filters.from).toISOString();
        }
        if (filters.to) {
            q.to = new Date(filters.to).toISOString();
        }

        return q;
    }, [page, size, filters.from, filters.to]);

    const totalPages = useMemo(() => {
        if (!auditData) {
            return 0;
        }
        const total = auditData.totalElements || 0;
        const sizeValue = auditData.size || DEFAULT_PAGE_SIZE;
        return Math.ceil(total / sizeValue);
    }, [auditData]);

    const loadLogs = useCallback(async () => {
        try {
            if (type === 'AUDIT') {
                const res = await axios.get<PagedResult<AuditLogEntry>>('/logs/audit', {params: query});
                setAuditData(res.data);
            } else {
                const res = await axios.get<string>('/logs/technical/export', {
                    params: technicalDate ? {date: technicalDate} : undefined,
                    responseType: 'text',
                });
                setTechnicalPreview(res.data as unknown as string);
            }
        } catch {
            // swallow
        }
    }, [type, query, technicalDate]);

    useEffect(() => {
        void loadLogs();
    }, [loadLogs]);

    const handleSearchAudit = () => {
        setPage(0);
        setFilters({
            from: fromInput || undefined,
            to: toInput || undefined,
        });
    };

    const handleAuditFiltersSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        handleSearchAudit();
    };

    const downloadFromResponse = (data: Blob, headers: Record<string, string>, fallbackName: string) => {
        let filename = fallbackName;
        const cd = headers['content-disposition'] || headers['Content-Disposition'];

        if (typeof cd === 'string') {
            const match = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
            const encoded = match?.[1] || match?.[2];

            if (encoded) {
                try {
                    filename = decodeURIComponent(encoded);
                } catch {
                    filename = encoded;
                }
            }
        }

        const url = window.URL.createObjectURL(data);
        const a = document.createElement('a');

        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);
    };

    const handleExport = async () => {
        if (type === 'AUDIT') {
            const dateParam = fromInput ? fromInput.substring(0, 10) : undefined;
            const res = await axios.get('/logs/audit/export', {
                responseType: 'blob',
                params: dateParam ? {date: dateParam} : undefined,
            });
            downloadFromResponse(res.data, res.headers as Record<string, string>, 'audit.log');
            return;
        }

        const res = await axios.get('/logs/technical/export', {
            responseType: 'blob',
            params: technicalDate ? {date: technicalDate} : undefined,
        });
        downloadFromResponse(res.data, res.headers as Record<string, string>, 'technical.log');
    };

    const handlePageSizeChange = (newSize: number) => {
        setSize(newSize);
        setPage(0);
    };

    const technicalLines = useMemo(() => {
        const all = (technicalPreview || '').split(/\r?\n/);

        if (!technicalSearchTags.length) {
            return all;
        }

        const tags = technicalSearchTags.map((t) => t.toLowerCase());
        return all.filter((l) => {
            const lower = l.toLowerCase();
            return tags.every((tag) => lower.includes(tag));
        });
    }, [technicalPreview, technicalSearchTags]);

    const handleTechnicalTagsChange = (tags: string[]) => {
        setTechnicalSearchTags(tags);
        try {
            sessionStorage.setItem(TECHNICAL_LOGS_SEARCH_TAGS, JSON.stringify(tags));
        } catch {
            // eslint-disable-next-line no-console
            console.warn('Failed to save technical logs search tags to session storage');
        }
    };

    const handleTabChange = (newType: LogTypeLocal) => {
        setType(newType);
        setPage(0);
    };

    const handleFromChange = (value: Date | null) => {
        if (value && !isNaN(value.getTime())) {
            setFromInput(formatDateTimeLocal(value));
        } else {
            setFromInput('');
        }
    };

    const handleToChange = (value: Date | null) => {
        if (value && !isNaN(value.getTime())) {
            setToInput(formatDateTimeLocal(value));
        } else {
            setToInput('');
        }
    };

    const handleTechnicalDateChange = (value: Date | null) => {
        if (value && !isNaN(value.getTime())) {
            setTechnicalDate(formatDateOnly(value));
        } else {
            setTechnicalDate('');
        }
    };

    return (
        <StyledTableContainer>
            <Header>
                <h2>{t('logs.title', {defaultValue: 'Logs'})}</h2>
            </Header>

            <TabsBar>
                <TabButton $active={type === 'AUDIT'} onClick={() => handleTabChange('AUDIT')}>
                    {t('logs.audit')}
                </TabButton>
                 {/* [AI-769] [To be possibly uncommented in the future] */}
                {/*<TabButton $active={type === 'TECHNICAL'} onClick={() => handleTabChange('TECHNICAL')}>*/}
                {/*    {t('logs.technical')}*/}
                {/*</TabButton>*/}
            </TabsBar>

            <FiltersBar as="form" onSubmit={handleAuditFiltersSubmit}>
                {type === 'AUDIT' && (
                    <>
                        <Label>
                            <FieldLabel>{t('logs.from')}</FieldLabel>
                            <DateTimePicker
                                value={fromInput ? new Date(fromInput) : null}
                                ampm={is12h}
                                format={dateTimeFormat}
                                onChange={handleFromChange}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                    } as any,
                                }}
                            />
                        </Label>
                        <Label>
                            <FieldLabel>{t('logs.to')}</FieldLabel>
                            <DateTimePicker
                                value={toInput ? new Date(toInput) : null}
                                ampm={is12h}
                                format={dateTimeFormat}
                                onChange={handleToChange}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                    } as any,
                                }}
                            />
                        </Label>
                        <PageSizeField>
                            <FieldLabel>{t('logs.pageSize')}</FieldLabel>
                            <PageSizeSelector
                                options={[5, 10, 20, 50]}
                                selectedSize={size}
                                onChange={handlePageSizeChange}
                                ariaLabelGroup={t('pageSizeSelector.aria.group') as string}
                                getSizeAriaLabel={(size) =>
                                    t('pageSizeSelector.aria.setSize', {size}) as string
                                }
                            />
                        </PageSizeField>
                        <ActionField>
                            <ActionButton type="submit" disabled={loading}>
                                {t('common.search')}
                            </ActionButton>
                        </ActionField>
                    </>
                )}

                {/* [AI-769] [To be possibly uncommented in the future] */}
                {/*{type === 'TECHNICAL' && (*/}
                {/*    <ControlsRow style={{alignItems: 'flex-end'}}>*/}
                {/*        <Label style={{width: 200}}>*/}
                {/*            <span>{t('logs.date')}</span>*/}
                {/*            <DatePicker*/}
                {/*                value={technicalDate ? new Date(technicalDate) : null}*/}
                {/*                format={dateFormat}*/}
                {/*                onChange={handleTechnicalDateChange}*/}
                {/*                slotProps={{*/}
                {/*                    textField: {*/}
                {/*                        size: 'small',*/}
                {/*                        fullWidth: true,*/}
                {/*                    } as any,*/}
                {/*                }}*/}
                {/*            />*/}
                {/*        </Label>*/}

                {/*        <SearchWrapper style={{flex: 1, marginBottom: 4}}>*/}
                {/*            <SearchWithTagsInput*/}
                {/*                onTagsChange={handleTechnicalTagsChange}*/}
                {/*                initialTags={technicalSearchTags}*/}
                {/*                ariaLabel={t('searchWithTagsInput.ariaLabel') as string}*/}
                {/*            />*/}
                {/*        </SearchWrapper>*/}

                {/*        <Label style={{marginTop: 'auto'}}>*/}
                {/*            <span style={{visibility: 'hidden'}}>_</span>*/}
                {/*            <div style={{display: 'flex', gap: 8}}>*/}
                {/*                <ActionButton onClick={() => void loadLogs()} disabled={loading}>*/}
                {/*                    {t('common.refresh')}*/}
                {/*                </ActionButton>*/}
                {/*                <ActionButton*/}
                {/*                    onClick={() => void handleExport()}*/}
                {/*                    disabled={loading}*/}
                {/*                    aria-label={t('logs.export') as string}*/}
                {/*                >*/}
                {/*                    {t('logs.export')}*/}
                {/*                </ActionButton>*/}
                {/*            </div>*/}
                {/*        </Label>*/}
                {/*    </ControlsRow>*/}
                {/*)}*/}
            </FiltersBar>

            {type === 'AUDIT' && (
                <>
                    <Table>
                        <thead>
                        <tr>
                            <Th>{t('logs.timestamp')}</Th>
                            <Th>{t('logs.username')}</Th>
                            <Th>{t('logs.action')}</Th>
                            <Th>{t('logs.entityType')}</Th>
                            <Th>{t('logs.entityId')}</Th>
                            <Th>{t('logs.details')}</Th>
                        </tr>
                        </thead>
                        <tbody>
                        {auditData?.content?.length ? (
                            auditData.content.map((row, idx) => (
                                <tr key={idx}>
                                    <Td>{new Date(row.timestamp).toLocaleString()}</Td>
                                    <Td>{row.username}</Td>
                                    <Td>{row.action}</Td>
                                    <Td>{row.entityType}</Td>
                                    <Td>{row.entityId}</Td>
                                    <Td>{row.details}</Td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <Td colSpan={6}>{t('logs.noData')}</Td>
                            </tr>
                        )}
                        </tbody>
                    </Table>

                    <PaginationWrapper>
                        <Pagination
                            count={Math.max(totalPages, 1)}
                            page={Math.min(page + 1, Math.max(totalPages, 1))}
                            onChange={(_, value) => {
                                if (!loading) setPage(Math.max(0, value - 1));
                            }}
                            color="primary"
                        />
                    </PaginationWrapper>
                </>
            )}
            {/* [AI-769] [To be possibly uncommented in the future] */}
            {/*{type === 'TECHNICAL' && (*/}
            {/*    <LogViewer>*/}
            {/*        {technicalPreview*/}
            {/*            ? technicalLines.map((line, idx) => {*/}
            {/*                const level = getLevel(line);*/}
            {/*                return (*/}
            {/*                    <LogLine key={idx}>*/}
            {/*                        <LineNo>{idx + 1}</LineNo>*/}
            {/*                        <div>*/}
            {/*                            {level ? <Level $level={level}>[{level}] </Level> : null}*/}
            {/*                            <span>{line}</span>*/}
            {/*                        </div>*/}
            {/*                    </LogLine>*/}
            {/*                );*/}
            {/*            })*/}
            {/*            : t('logs.noData')}*/}
            {/*    </LogViewer>*/}
            {/*)}*/}
        </StyledTableContainer>
    );
};

export default Logs;
